import { ChangeEvent, useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import JicFloatingWidget from "./jicFloatingWidget";
import LoveTransferPanel from "./loveTransferPanel";
import { describeFace, loadFaceRecognitionModels } from "../lib/faceRecognition";
import { resolveFaceOnServer } from "../lib/faceRegistry";
import { getJicToken, getJicUser, loginJic } from "../lib/jic";

const JIC_URL = "https://u.jic.io";
const LOVE_ADDRESS = "0x16A7F8CfAD687A87183fCbd1dF7aF09dce05D357";
const USDC_ADDRESS = "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913";

type Anchor = {
  slug: string;
  label: string;
  kind: "relay" | "prereg";
  poolAddress: string | null;
  note: string | null;
  templates: number;
};

// 每个锚点站的主题:图标/称号/文案/公有领域画廊(展示用,与识别模板无关)
type ShrineTheme = {
  icon: string;
  title: string;
  scanHint: string;
  intro: string;
  gallery: { src: string; title: string }[];
};
const SHRINE_THEMES: Record<string, ShrineTheme> = {
  jesus: {
    icon: "✟",
    title: "Jesus",
    scanHint: "扫任何经典基督画像",
    intro: "没有人能「是」这个锚点 —— 它属于所有人。扫描任何一幅经典基督画像即可致敬:投入的 LOVE 进入公共池,按透明规则流出成为祝福。",
    gallery: [
      { src: "https://upload.wikimedia.org/wikipedia/commons/d/d8/Christ_Icon_Sinai_6th_century_-_crop.jpg", title: "西奈基督圣像 · 6 世纪" },
      { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5c/Leonardo_da_Vinci%2C_Salvator_Mundi%2C_c.1500%2C_oil_on_walnut%2C_45.4_%C3%97_65.6_cm.jpg/960px-Leonardo_da_Vinci%2C_Salvator_Mundi%2C_c.1500%2C_oil_on_walnut%2C_45.4_%C3%97_65.6_cm.jpg", title: "达芬奇《救世主》· c.1500" },
      { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d1/Rembrandt_Harmensz._van_Rijn%2C_Dutch_%28active_Leiden_and_Amsterdam%29_-_Head_of_Christ_-_Google_Art_Project.jpg/960px-Rembrandt_Harmensz._van_Rijn%2C_Dutch_%28active_Leiden_and_Amsterdam%29_-_Head_of_Christ_-_Google_Art_Project.jpg", title: "伦勃朗《基督头像》· c.1648" },
    ],
  },
  wukong: {
    icon: "🐒",
    title: "齐天大圣",
    scanHint: "扫任何孙悟空形象",
    intro: "五百年山下,一根金箍棒。扫描任何一个孙悟空的形象——扮相、塑像、绘画——即可致敬:投入的 LOVE 进入公共池,按透明规则流出成为祝福。",
    gallery: [
      { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/%E5%AD%AB%E6%82%9F%E7%A9%BA-The_Monkey_King_Songok%C5%AB%2C_from_the_Chinese_novel_Journey_to_the_West_MET_DP135893.jpg/960px-%E5%AD%AB%E6%82%9F%E7%A9%BA-The_Monkey_King_Songok%C5%AB%2C_from_the_Chinese_novel_Journey_to_the_West_MET_DP135893.jpg", title: "月冈芳年《孙悟空》浮世绘 · 1889" },
      { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/75/Xiyou.PNG/960px-Xiyou.PNG", title: "《西游记》古典绣像" },
      { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/Sun_Wukong_by_Bi_Heng.jpg/960px-Sun_Wukong_by_Bi_Heng.jpg", title: "毕横《孙悟空》" },
    ],
  },
  caishen: {
    icon: "🧧",
    title: "财神",
    scanHint: "扫任何财神形象",
    intro: "恭喜发财。扫描任何一幅财神画像——年画、塑像、庙宇壁画——即可致敬:投入的 LOVE 进入公共池,按透明规则流出成为祝福。",
    gallery: [],
  },
  guanyin: {
    icon: "🪷",
    title: "观音",
    scanHint: "扫任何观音形象",
    intro: "慈悲为怀,寻声救苦。扫描任何一尊观音的形象——造像、画像、壁画——即可致敬:投入的 LOVE 进入公共池,按透明规则流出成为祝福。",
    gallery: [],
  },
  jigong: {
    icon: "🍶",
    title: "济公",
    scanHint: "扫任何济公形象",
    intro: "鞋儿破,帽儿破,袈裟破,心不破。扫描任何一个济公的形象——塑像、画像、扮相——即可致敬:投入的 LOVE 进入公共池,按透明规则流出成为祝福。",
    gallery: [],
  },
};
const DEFAULT_THEME = (slug: string): ShrineTheme => ({
  icon: "⚓",
  title: slug,
  scanHint: "扫 TA 的形象",
  intro: "扫描这个锚点的形象即可致敬:投入的 LOVE 进入公共池,按透明规则流出成为祝福。",
  gallery: [],
});

function shortAddress(value: string) {
  return `${value.slice(0, 7)}…${value.slice(-5)}`;
}

export default function AnchorShrine({ slug }: { slug: string }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);
  const analyzingRef = useRef(false);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [anchorChecked, setAnchorChecked] = useState(false);
  const [pool, setPool] = useState<{ usdc: number; love: number; nav: number } | null>(null);
  const [flows, setFlows] = useState<{ in: { LOVE?: number; USDC?: number }; out: { LOVE?: number; USDC?: number } } | null>(null);
  const [blessCount, setBlessCount] = useState<number | null>(null);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollMsg, setEnrollMsg] = useState("");
  const [bless, setBless] = useState<{ state: "idle" | "claiming" | "done" | "already" | "error"; text: string; tx?: string | null }>({ state: "idle", text: "" });
  const [scanning, setScanning] = useState(false);
  const [recognized, setRecognized] = useState<number | null>(null); // confidence
  const [message, setMessage] = useState("扫描或上传任何一幅经典基督画像");

  useEffect(() => {
    loadFaceRecognitionModels().catch(() => undefined);
    fetch(`${JIC_URL}/v1/face/anchors`)
      .then((response) => response.json())
      .then((data: { anchors?: Anchor[] }) => {
        setAnchor(data.anchors?.find((item) => item.slug === slug) || null);
      })
      .catch(() => setAnchor(null))
      .finally(() => setAnchorChecked(true));
    return stopScanner;
  }, [slug]);

  // 公共池链上余额(只读 RPC,USDC + LOVE + NAV 折算,30s 自动刷新)
  useEffect(() => {
    if (!anchor?.poolAddress) return;
    const address = anchor.poolAddress;
    const provider = new ethers.JsonRpcProvider("https://mainnet.base.org", 8453, { staticNetwork: true });
    const abi = ["function balanceOf(address) view returns (uint256)"];
    const love = new ethers.Contract(LOVE_ADDRESS, abi, provider);
    const usdc = new ethers.Contract(USDC_ADDRESS, abi, provider);
    let cancelled = false;
    const refresh = async () => {
      try {
        const [loveRaw, usdcRaw, navData, flowData] = await Promise.all([
          love.balanceOf(address) as Promise<bigint>,
          usdc.balanceOf(address) as Promise<bigint>,
          fetch(`${JIC_URL}/v1/nav`).then((r) => r.json()).catch(() => null),
          fetch(`${JIC_URL}/v1/face/anchor/${slug}/flows`).then((r) => r.json()).catch(() => null),
        ]);
        if (cancelled) return;
        setPool({
          love: Number(ethers.formatUnits(loveRaw, 18)),
          usdc: Number(ethers.formatUnits(usdcRaw, 6)),
          nav: typeof navData?.nav === "number" ? navData.nav : 1,
        });
        if (flowData?.in || flowData?.out) setFlows({ in: flowData.in || {}, out: flowData.out || {} });
      } catch {
        // 保留上一次数值
      }
    };
    refresh();
    const id = window.setInterval(refresh, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [anchor?.poolAddress]);

  useEffect(() => {
    fetch(`${JIC_URL}/v1/face/anchor/${slug}/blessings`)
      .then((r) => r.json())
      .then((d: { claims?: number }) => setBlessCount(typeof d.claims === "number" ? d.claims : null))
      .catch(() => undefined);
  }, [slug]);

  // 领取祝福:登录 JIC 后调用;登录跳转回来若有待领标记则自动补领
  const claimBlessing = async () => {
    const token = getJicToken();
    if (!token) {
      try { sessionStorage.setItem("face3:bless-pending", slug); } catch { /* 忽略 */ }
      loginJic({ clearLocal: false });
      return;
    }
    setBless({ state: "claiming", text: "正在领取祝福…" });
    try {
      const response = await fetch(`${JIC_URL}/v1/face/anchor/${slug}/bless`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as { ok?: boolean; already?: boolean; amount?: number; paid?: boolean; tx?: string | null; error?: string; todayIndex?: number; faceBound?: boolean };
      if (!data.ok) {
        setBless({ state: "error", text: data.error === "daily cap reached" ? "今日 100 份祝福已领完，明天再来" : data.error || "领取失败" });
        return;
      }
      const amountText = `${(data.amount || 0).toFixed(5)} LOVE`;
      if (data.already) {
        setBless({ state: "already", text: `你已领取过这份祝福（${amountText}${data.paid ? "，已到账" : "，圣池结算中"}）`, tx: data.tx });
      } else {
        setBless({
          state: "done",
          text: data.paid
            ? `${amountText} 已到账${data.faceBound ? " · 一脸一人 10 倍祝福" : ""} · 今日第 ${data.todayIndex} 位`
            : `${amountText} 已登记，圣池批量结算中${data.faceBound ? " · 一脸一人 10 倍祝福" : ""} · 今日第 ${data.todayIndex} 位`,
          tx: data.tx,
        });
        setBlessCount((count) => (count === null ? count : count + 1));
      }
    } catch {
      setBless({ state: "error", text: "网络异常，请重试" });
    }
  };

  useEffect(() => {
    try {
      if (sessionStorage.getItem("face3:bless-pending") === slug && getJicToken()) {
        sessionStorage.removeItem("face3:bless-pending");
        setRecognized(100);
        setMessage("登录完成，正在补领祝福…");
        void claimBlessing();
      }
    } catch { /* 忽略 */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // 一切以 LOVE 计量:USDC 按 NAV 折算并入 LOVE 等值
  const nav = pool?.nav || 1;
  const poolLove = pool ? pool.love + pool.usdc / nav : null;
  const flowLove = (f?: { LOVE?: number; USDC?: number }) => (f ? (f.LOVE || 0) + (f.USDC || 0) / nav : 0);
  const givenLove = flows ? flowLove(flows.in) : null;    // 累计致敬(流入)
  const blessedLove = flows ? flowLove(flows.out) : null; // 已分发祝福(流出)
  const fmtLove = (v: number) => v.toFixed(5);

  const stopScanner = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setScanning(false);
  };

  const resolveInput = async (input: HTMLVideoElement | HTMLImageElement) => {
    if (analyzingRef.current) return;
    analyzingRef.current = true;
    try {
      const descriptor = await describeFace(input);
      if (!descriptor) {
        setMessage("没有找到清晰面容，请对准画像再试");
        return;
      }
      const server = await resolveFaceOnServer(descriptor);
      if (server?.match && server.match.slug === slug) {
        setRecognized(server.match.confidence);
        setMessage(`识别成功 · 置信度 ${server.match.confidence}%`);
        stopScanner();
      } else if (server?.match) {
        setMessage(`识别到的是 ${server.match.label || "@" + server.match.name}，不是这个锚点`);
      } else {
        setMessage("检测到面容，但与本锚点不匹配");
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "识别失败");
    } finally {
      analyzingRef.current = false;
    }
  };

  const startCamera = async () => {
    stopScanner();
    setRecognized(null);
    setMessage("正在打开相机…");
    try {
      await loadFaceRecognitionModels();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      if (!videoRef.current) throw new Error("相机初始化失败");
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setScanning(true);
      setMessage("对准画像、雕像或屏幕上的基督面容…");
      timerRef.current = window.setInterval(() => {
        if (videoRef.current && videoRef.current.readyState >= 2) resolveInput(videoRef.current);
      }, 900);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "无法打开相机");
      stopScanner();
    }
  };

  const onPhoto = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    stopScanner();
    setRecognized(null);
    setMessage("正在识别画像…");
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = async () => {
      try {
        await resolveInput(image);
      } finally {
        URL.revokeObjectURL(objectUrl);
        event.target.value = "";
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setMessage("照片读取失败，请换一张");
    };
    image.src = objectUrl;
  };

  const label = anchor?.label || "LOVE 中转站";
  const theme = SHRINE_THEMES[slug] || DEFAULT_THEME(slug);


  // 策展人传照片补录识别模板
  const onCuratorPhoto = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setEnrolling(true);
    setEnrollMsg("正在认图里的脸…");
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = async () => {
      try {
        const descriptor = await describeFace(image);
        if (!descriptor) throw new Error("图里没认出清晰的脸,换一张(尽量选近似人脸的形象)");
        const response = await fetch(`${JIC_URL}/v1/face/anchor/${slug}/descriptor`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getJicToken()}` },
          body: JSON.stringify({ descriptor }),
        });
        const data = (await response.json()) as { ok?: boolean; anchor?: { templates: number }; error?: string };
        if (!data.ok) throw new Error(data.error === "curator or admin only" ? "只有这个锚点的策展人能录模板" : "没存上,再试一次");
        setAnchor((prev) => (prev ? { ...prev, templates: data.anchor?.templates ?? prev.templates + 1 } : prev));
        setEnrollMsg(`✓ 已录入 ${data.anchor?.templates ?? "?"} 张`);
      } catch (error) {
        setEnrollMsg(error instanceof Error ? error.message : "没认出来");
      } finally {
        URL.revokeObjectURL(objectUrl);
        event.target.value = "";
        setEnrolling(false);
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setEnrolling(false);
      setEnrollMsg("图片打不开");
    };
    image.src = objectUrl;
  };
  const viewer = getJicUser()?.sub?.toLowerCase();
  const canEnroll = Boolean(viewer && getJicToken());

  return (
    <main className="face3-page face3-scan-page">
      <div className="face3-aurora face3-aurora-one" />
      <div className="face3-aurora face3-aurora-two" />
      <header className="face3-header">
        <a className="face3-brand" href="https://face.jic.io" aria-label="FACE3 home"><span className="face3-mark">{theme.icon}</span><span>{slug.toUpperCase()} · JIC</span></a>
        <span className="face3-mvp-pill">RELAY · LOVE</span>
      </header>

      <section className="face3-scan-hero">
        <div className="face3-scan-copy">
          <p className="face3-eyebrow">LOVE RELAY STATION</p>
          <h1>{theme.title}<br /><em>LOVE 中转站</em></h1>
          <p>{theme.intro}</p>
          <div className="face3-scan-actions">
            <button type="button" onClick={startCamera}>⌁ {theme.scanHint}</button>
            <button type="button" className="secondary" onClick={() => fileRef.current?.click()}>从照片识别</button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} hidden />
          </div>
          <div className="face3-local-registry">
            <i />
            {anchorChecked && !anchor && "锚点不存在"}
            {anchor && (
              <>公共池：{anchor.poolAddress ? shortAddress(anchor.poolAddress) : "地址待设定"} · 模板 {anchor.templates} 幅</>
            )}
          </div>
        </div>

        <div className={`face3-scanner ${scanning ? "is-scanning" : ""}`}>
          <video ref={videoRef} playsInline muted />
          <div className="face3-scan-grid" />
          <div className="face3-scan-corners"><i /><i /><i /><i /></div>
          {!scanning && !recognized && (
            <div className="face3-scan-placeholder">
              <span>{theme.icon}</span>
              <strong>{slug.toUpperCase()} RELAY</strong>
              <small>{theme.scanHint} · 不需要活体</small>
            </div>
          )}
          {scanning && <div className="face3-scan-line" />}
          <div className={`face3-scan-result state-${recognized ? "matched" : scanning ? "scanning" : "idle"}`}>
            <i />
            <div><strong>{recognized ? `${theme.icon} ${label}` : scanning ? "SCANNING" : "READY"}</strong><small>{message}</small></div>
          </div>
        </div>
      </section>

      {anchor && anchor.templates === 0 && canEnroll && (
        <section className="face3-bless-card" aria-label="补录模板">
          <div className="face3-bless-copy">
            <strong>📸 这个锚点还认不出任何形象</strong>
            <small>策展人传一张近似人脸的照片(扮相/塑像/画像),扫描才能命中</small>
          </div>
          <label className="face3-pool-cta" style={{ margin: 0, display: "grid", placeItems: "center", cursor: "pointer" }}>
            {enrolling ? "认脸中…" : "传一张照片"}
            <input type="file" accept="image/*" onChange={onCuratorPhoto} hidden />
          </label>
          {enrollMsg && <div className="face3-bless-status">{enrollMsg}</div>}
        </section>
      )}

      {anchor?.poolAddress && (
        <section className="face3-pool-panel" aria-label="圣池实时余额">
          <p className="face3-eyebrow">THE POOL · 圣池实时余额</p>
          <div className="face3-pool-total">
            {poolLove === null ? "…" : fmtLove(poolLove)}<em>LOVE</em>
          </div>
          {flows && (
            <div className="face3-pool-flows">
              <span>↑ 累计致敬 <b>{fmtLove(givenLove ?? 0)}</b> LOVE</span>
              <i />
              <span className="is-blessed">↓ 已分发祝福 <b>{fmtLove(blessedLove ?? 0)}</b> LOVE</span>
              {blessCount !== null && <><i /><span className="is-blessed">🕊 已赐福 <b>{blessCount}</b> 人</span></>}
            </div>
          )}
          <a className="face3-pool-link" href={`https://basescan.org/address/${anchor.poolAddress}`} target="_blank" rel="noreferrer">
            {shortAddress(anchor.poolAddress)} · 链上实时可查 ↗
          </a>
          <button type="button" className="face3-pool-cta" onClick={() => { window.scrollTo({ top: 0, behavior: "smooth" }); startCamera(); }}>
            {theme.icon} {theme.scanHint} · 向圣池致敬
          </button>
        </section>
      )}

      {recognized !== null && anchor && (
        <section className="face3-bless-card" aria-label="领取祝福">
          <div className="face3-bless-copy">
            <strong>🕊 圣池赐福</strong>
            <small>识别成功即可领取 · 每人一次 · 每日限前 100 位 · 绑定人脸的真人 10 倍</small>
          </div>
          <button type="button" className="face3-pool-cta" onClick={claimBlessing} disabled={bless.state === "claiming" || bless.state === "done"}>
            {bless.state === "claiming" ? "领取中…" : bless.state === "done" ? "✓ 已领取" : getJicToken() ? "🕊 领取祝福" : "登录 JIC 领取祝福"}
          </button>
          {bless.text && (
            <div className={`face3-bless-status is-${bless.state}`}>
              {bless.text}
              {bless.tx && <> · <a href={`https://basescan.org/tx/${bless.tx}`} target="_blank" rel="noreferrer">链上查看 ↗</a></>}
            </div>
          )}
        </section>
      )}

      {recognized !== null && anchor && (
        <LoveTransferPanel
          faceId={label}
          handle={anchor.slug}
          recipient={anchor.poolAddress || undefined}
          verified={Boolean(anchor.poolAddress)}
        />
      )}

      <section className="face3-scan-explainer">
        {theme.gallery.map((art) => (
          <div key={art.src} style={{ display: "block", padding: 0, overflow: "hidden" }}>
            <img src={art.src} alt={art.title} style={{ display: "block", width: "100%", aspectRatio: "1/1.1", objectFit: "cover" }} loading="lazy" />
            <small style={{ display: "block", padding: "10px 14px", color: "#718895" }}>{art.title}</small>
          </div>
        ))}
      </section>

      <section className="face3-flow">
        <div><span>01</span><strong>扫画像</strong><small>任何时代的基督面容</small></div>
        <i>→</i>
        <div><span>02</span><strong>投入公共池</strong><small>致敬 · 钱包逐笔签名</small></div>
        <i>→</i>
        <div><span>03</span><strong>透明流出</strong><small>按策展规则成为祝福</small></div>
        <i>→</i>
        <div><span>♡</span><strong>正和循环</strong><small>创世 · 路由 · 激励皆供能 NAV</small></div>
      </section>

      <footer className="face3-footer">
        <span>{slug}.jic.io · A LOVE relay, owned by no one, open to everyone.</span>
        <span>公有领域画像 · Wikimedia Commons</span>
      </footer>
      <JicFloatingWidget icon="🕊" gold />
    </main>
  );
}
